/**
 * $TOKEN token admin panel.
 *
 * Shell only — tenant selector plus a tab strip, delegating to the feature
 * components. Same shape as Bonus2Admin.tsx.
 *
 * TWO INDEPENDENT GATES, and both matter:
 *
 *   PERMISSIONS.PAGE_TOKEN  — decides WHO may open this page. Enforced by
 *                             ProtectedRoute in App.tsx and by the nav item.
 *                             PAGE_TOKEN_BULK_GRANT gates one further tab; it
 *                             is NOT implied by PAGE_TOKEN.
 *   TokenGateBoundary       — decides WHICH TENANTS have anything behind it
 *                             (`tokenEnabled && isMicroserviceOwner`, resolved
 *                             server-side). Resolved here, before the tab strip
 *                             renders, so a tenant without $TOKEN shows one
 *                             explanation rather than four tabs that each fail.
 *
 * Neither is a security boundary — every endpoint re-checks the gate on every
 * call. These stop confusion, not a determined caller.
 */

import { ChangeEvent, useEffect, useState } from "react"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { WelcomeSection } from "components/general/WelcomeSection"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { TopBar } from "components/navigation/TopBar"

import { TokenGateBoundary } from "features/token/components/shared/TokenGateBoundary"
import { TOKEN_TABS, TokenTabType, Translate } from "features/token/helpers/token.helpers"
import { TOKEN_TAB_CONTENT } from "features/token/helpers/token.tabs"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import { tenantOptions } from "constants/bonus"
import { PERMISSIONS } from "constants/permissions"

export const TokenAdmin = () => {
  const { t } = useTranslation("translation", { useSuspense: false }) as { t: Translate }
  const { user } = useProps()
  const { has } = usePermissions()

  // Bulk grant is not part of the PAGE_TOKEN surface — mass minting is a
  // separate privilege, so the tab is absent rather than disabled for an admin
  // who does not hold it.
  const tabs = TOKEN_TABS.filter((tab) => tab.id !== "bulk-grant" || has(PERMISSIONS.PAGE_TOKEN_BULK_GRANT))

  const [currentTab, setCurrentTab] = useState<TokenTabType>("presale")
  const [tenant, setTenant] = useState<string>("")

  useEffect(() => {
    if (!tenant && tenantOptions.length > 0) {
      setTenant(tenantOptions[0].value)
    }
  }, [])

  const handleTenantChange = (ev: ChangeEvent<HTMLSelectElement>) => {
    setTenant(ev.target.value)
  }

  const TabComponent = TOKEN_TAB_CONTENT[currentTab]

  return (
    <div className="grid w-full gap-8 p-8 pt-0 grid-col-1">
      <TopBar title={t("token.pageTitle")} />
      {user && <WelcomeSection />}

      <div className="flex items-center gap-4">
        <label className="font-medium text-white">{t("token.tenantLabel")}</label>
        <DropdownMenu placeholder={t("token.tenantPlaceholder")} options={tenantOptions} value={tenant} onChange={handleTenantChange} rounded />
      </div>

      {!tenant ? (
        <div className="py-12 text-center text-gray-400">{t("token.selectTenantPrompt")}</div>
      ) : (
        // `key` forces a fresh probe when the operator switches tenant — without
        // it a move from an enabled tenant to a disabled one would keep rendering
        // the tabs of the previous tenant's gate result.
        <TokenGateBoundary key={tenant} tenant={tenant}>
          <div className="tabs gap-2">
            {tabs.map((tab) => (
              <a
                key={tab.id}
                onClick={() => setCurrentTab(tab.id)}
                className={clsx(
                  "flex cursor-pointer items-center text-left py-3 rounded-lg px-4 text-base border text-[rgba(6,7,21,1)] dark:text-white border-[rgba(132,_0,_168,_1)] dark:border-transparent transition-all dark:bg-[#0C0C1A] hover:border-[rgba(255,255,255,0.1)]",
                  {
                    "transition-all text-white bg-[rgba(132,_0,_168,_1)] dark:bg-[rgba(132,_0,_168,_1)]": currentTab === tab.id,
                    "hover:border-[rgba(132,_0,_168,_0.8)] hover:dark:border-[rgba(132,_0,_168,_0.8)] hover:bg-[rgba(169,78,206,0.2)] dark:hover:bg-[rgba(169,78,206,0.15)]":
                      currentTab !== tab.id
                  }
                )}
              >
                {t(tab.labelKey)}
              </a>
            ))}
          </div>

          <TabComponent tenant={tenant} />
        </TokenGateBoundary>
      )}
    </div>
  )
}
